import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../utils/api';

/**
 * Correction-signal panel: what the AI initially extracted vs. the invoice now.
 * The differences are the accountant's corrections (learning-loop raw signal).
 */
export function ExtractionDiffPanel({ invoiceId }) {
  const [diff, setDiff] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    apiFetch(`/api/invoices/${invoiceId}/extraction-diff`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) setDiff(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, [invoiceId]);

  if (!diff) return null;
  if (!diff.available) return null; // pre-feature invoice — show nothing

  const all = [...(diff.changes || []), ...(diff.line_changes || [])];
  const badge = all.length === 0
    ? '✓ identic cu extracția AI'
    : `${all.length} câmp(uri) corectate de contabil`;

  return (
    <div className="section-card" style={{ marginTop: '1rem' }}>
      <div className="section-header" style={{ cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        <h2 className="section-title" style={{ fontSize: '1.05rem' }}>
          Extracție AI vs. Final {open ? '▾' : '▸'}
        </h2>
        <p className="section-subtitle">
          {badge}
          {diff.captured_at ? ` · capturat ${new Date(diff.captured_at).toLocaleString('ro-RO')}` : ''}
          {diff.initial_confidence ? ` · încredere inițială: ${diff.initial_confidence}` : ''}
        </p>
      </div>
      {open && all.length > 0 && (
        <div className="section-content">
          <div className="lines-table-container">
            <table className="lines-table">
              <thead className="lines-table-head">
                <tr><th>Câmp</th><th>Extras de AI</th><th>Valoare finală</th></tr>
              </thead>
              <tbody className="lines-table-body">
                {all.map((c, i) => (
                  <tr key={i}>
                    <td>{c.field}</td>
                    <td style={{ opacity: 0.7 }}>{String(c.initial ?? '—')}</td>
                    <td style={{ fontWeight: 600 }}>{String(c.current ?? '—')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * ANAF submission audit panel: every submission attempt for this invoice,
 * successes and failures alike — the "was it sent twice?" answer.
 */
export function SubmissionAuditPanel({ invoiceId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    apiFetch(`/api/anaf/invoices/${invoiceId}/audit`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive) setData(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, [invoiceId]);

  if (!data || !data.attempts || data.attempts.length === 0) return null;

  const label = (a) => ({
    submit: a.success ? 'Trimisă' : 'Eșuată',
    failed: 'Eșuată',
    blocked_resend: 'Blocată (retrimitere)',
    blocked_collision: 'Blocată (număr existent în SPV)',
  }[a.action] || a.action);

  return (
    <div className="section-card" style={{ marginTop: '1rem' }}>
      <div className="section-header" style={{ cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        <h2 className="section-title" style={{ fontSize: '1.05rem' }}>
          Istoric trimiteri ANAF {open ? '▾' : '▸'}
        </h2>
        <p className="section-subtitle">{data.total} încercare(ări) înregistrate</p>
      </div>
      {open && (
        <div className="section-content">
          <div className="lines-table-container">
            <table className="lines-table">
              <thead className="lines-table-head">
                <tr><th>Data</th><th>Rezultat</th><th>Nr. factură</th><th>Upload ID</th><th>Detalii</th></tr>
              </thead>
              <tbody className="lines-table-body">
                {data.attempts.map((a) => (
                  <tr key={a.id}>
                    <td>{a.attempt_at ? new Date(a.attempt_at).toLocaleString('ro-RO') : '—'}</td>
                    <td style={{ color: a.success ? '#4caf7d' : '#ff6b6b', fontWeight: 600 }}>{label(a)}</td>
                    <td style={{ fontFamily: 'monospace' }}>{a.numar_factura || '—'}</td>
                    <td style={{ fontFamily: 'monospace' }}>{a.upload_id || '—'}</td>
                    <td style={{ maxWidth: 360, opacity: 0.8 }}>{a.error_message || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
